import {
  BestAvailableTransfersOrder,
  CategoryTypes,
  OrganizationType,
  rdbV1PersonSportRankingsList,
  rdbV1TransfersBestAvailableList,
  rdbV1TrendingListsList,
  rdbV2LatestList,
  rdbV2Nil100List,
  type SiteCategoryResponse,
  type SiteResponse,
  sitesV1SitesShowsDetail,
  SiteTemplate,
  type StateResponse,
  v1CategoriesFeaturedHeadlinesDetail,
  v1CategoriesHeadlinesPaginatedDetail,
  v1MenusDetail,
  v1OrganizationCompositeRankingsList,
  v1SitesForumsLatestPostsDetail,
  v1SitesHeadlinesForCategoryTypeDetail,
  v1SitesPremiumHeadlinesList,
  v1TeamRankingsRankingsList,
  v2OrganizationsScheduleDetail,
} from '@on3/api';
import { proxyApi } from '@on3/ui-lib/index';

const siteModules = {
  featuredArticles: v1CategoriesFeaturedHeadlinesDetail,
  categoryArticles: v1CategoriesHeadlinesPaginatedDetail,
  collegeRankings: v1TeamRankingsRankingsList,
  commitsList: rdbV2LatestList,
  nil100List: rdbV2Nil100List,
  quickLinks: v1MenusDetail,
  schedule: v2OrganizationsScheduleDetail,
  showsList: sitesV1SitesShowsDetail,
  siteArticlesByCategory: v1SitesHeadlinesForCategoryTypeDetail,
  sitePremiumArticles: v1SitesPremiumHeadlinesList,
  siteForums: v1SitesForumsLatestPostsDetail,
  topPlayerRankings: rdbV1PersonSportRankingsList,
  topTeamRankings: v1OrganizationCompositeRankingsList,
  trendingList: rdbV1TrendingListsList,
};

const getSiteDataByTemplateType = (
  templateType: SiteTemplate,
  site: SiteResponse,
  params?: {
    sportKey: number;
    year: number;
    stateAbbr?: string;
    stateKey?: number;
  },
) => {
  const {
    siteCategories,
    key: siteKey,
    siblingSiteKey,
    organizationKey,
    defaultTrendingListKey: trendingListKey,
  } = site;

  const getTrendingLists = () =>
    trendingListKey
      ? Promise.all([
          siteModules.trendingList(proxyApi, {
            trendingListKey,
            limit: 10,
          }),
          ...(trendingListKey !== 1
            ? [
                siteModules.trendingList(proxyApi, {
                  trendingListKey: 1,
                  limit: 10,
                }),
              ]
            : []),
        ])
      : Promise.resolve(null);

  const siteCategoryKey =
    siteCategories?.find(
      (category) => category.categoryType === CategoryTypes.Home,
    )?.categoryKey || 0;
  const siteOrganizationKey = organizationKey || 0;

  const includeHighSchoolArticles = [25, 74].includes(siteKey);

  // KSR
  const isKsrMinus = siteKey === 24;
  const isKsrPlus = siteKey === 395;

  // Field of 68
  const isFo68 = siteKey === 490;

  // Special conditions
  const showNil100 = ![432].includes(siteKey);
  const showQuickLinks = [432].includes(siteKey);

  switch (templateType) {
    case SiteTemplate.Traditional:
    case SiteTemplate.Rivals:
      if (isFo68) {
        return {
          featuredArticles: () =>
            siteModules.featuredArticles(proxyApi, siteCategoryKey, {
              timeout: 2000,
            }),
          collegeRankings: () =>
            siteModules.collegeRankings(proxyApi, {
              sportKey: params?.sportKey || 2,
              year: params?.year || new Date().getFullYear(),
              limit: 10,
              orgType: OrganizationType.College,
            }),
          showsList: () => siteModules.showsList(proxyApi, siteKey),
          siteForums: () => siteModules.siteForums(proxyApi, siteKey),
          topPlayerRankings: () =>
            siteModules.topPlayerRankings(proxyApi, {
              sportKey: params?.sportKey || 2,
              year: params?.year || new Date().getFullYear(),
              limit: 5,
            }),
          transferPlayerRankings: () =>
            rdbV1TransfersBestAvailableList(proxyApi, {
              sportKey: params?.sportKey || 2,
              year: params?.year || new Date().getFullYear(),
              orderBy: BestAvailableTransfersOrder.Industry,
            }).then((res) => ({ ...res, list: res.list?.slice(0, 5) })),
        };
      }

      return {
        featuredArticles: () =>
          siteModules.featuredArticles(proxyApi, siteCategoryKey, {
            timeout: 2000,
          }),
        sitePremiumArticles: () =>
          siteModules.sitePremiumArticles(proxyApi, { siteKey }),
        siteSportsArticles: isKsrPlus
          ? () =>
              siteModules.siteArticlesByCategory(proxyApi, {
                siteKey: 24,
                type: CategoryTypes.Sport,
                limit: 13,
              })
          : undefined,
        siteRecruitingArticles: isKsrPlus
          ? undefined
          : () =>
              siteModules.siteArticlesByCategory(proxyApi, {
                siteKey,
                type: CategoryTypes.Recruiting,
                limit: 13,
              }),
        siteHighSchoolArticles: includeHighSchoolArticles
          ? () =>
              siteModules.siteArticlesByCategory(proxyApi, {
                siteKey,
                type: CategoryTypes.HighSchool,
                limit: 13,
              })
          : undefined,
        siteForums: () => siteModules.siteForums(proxyApi, siteKey),
        channelSportsArticles: isKsrPlus
          ? undefined
          : () =>
              siteModules.siteArticlesByCategory(proxyApi, {
                siteKey: siblingSiteKey || 0,
                type: CategoryTypes.Sport,
                limit: 5,
              }),
        channelRecruitingArticles: isKsrPlus
          ? undefined
          : () =>
              siteModules.siteArticlesByCategory(proxyApi, {
                siteKey: siblingSiteKey || 0,
                type: CategoryTypes.Recruiting,
                limit: 5,
              }),
        commitsList: () =>
          siteModules.commitsList(proxyApi, {
            orgKey: siteOrganizationKey,
            limit: 5,
          }),
        nil100List: showNil100
          ? () =>
              siteModules.nil100List(proxyApi, {
                orgKey: siteOrganizationKey,
                limit: 5,
              })
          : undefined,
        schedule: () =>
          siteModules.schedule(proxyApi, {
            orgKey: siteOrganizationKey,
            sportKey: 1,
          }),
        trendingList: () => getTrendingLists(),
      };
    case SiteTemplate.TraditionalExtended:
      return {
        featuredArticles: () =>
          siteModules.featuredArticles(proxyApi, siteCategoryKey, {
            timeout: 2000,
          }),
        feedArticles: () =>
          siteModules.categoryArticles(proxyApi, {
            category: siteCategoryKey,
            limit: 18,
          }),
        channelSportsArticles: () =>
          siteModules.siteArticlesByCategory(proxyApi, {
            siteKey: siblingSiteKey || 0,
            type: CategoryTypes.Sport,
            limit: 5,
          }),
        channelRecruitingArticles: () =>
          siteModules.siteArticlesByCategory(proxyApi, {
            siteKey: siblingSiteKey || 0,
            type: CategoryTypes.Recruiting,
            limit: 5,
          }),
        messageBoard: () => siteModules.siteForums(proxyApi, siteKey),
        quickLinks: showQuickLinks
          ? () => siteModules.quickLinks(proxyApi, 583666) // Quick Links for UNC - @TODO: Make this dynamic
          : undefined,
        latestCommits: () =>
          siteModules.commitsList(proxyApi, {
            orgKey: siteOrganizationKey,
            limit: 5,
          }),
        schedule: () =>
          siteModules.schedule(proxyApi, {
            orgKey: siteOrganizationKey,
            sportKey: 1,
          }),
        trendingList: () => getTrendingLists(),
      };
    case SiteTemplate.Feed:
      return {
        feedArticles: () =>
          siteModules.categoryArticles(proxyApi, {
            category: siteCategoryKey,
            limit: 25,
          }),
        featuredArticles: () =>
          siteModules.siteArticlesByCategory(proxyApi, {
            siteKey: isKsrMinus ? 395 : siteKey,
            type: CategoryTypes.Home,
            limit: 5,
          }),
        featuredArticlesSecondary: () =>
          siteModules.siteArticlesByCategory(proxyApi, {
            siteKey: isKsrMinus ? 23 : siblingSiteKey || 0,
            type: CategoryTypes.Home,
            limit: 5,
          }),
        siteForums: () =>
          siteModules.siteForums(proxyApi, isKsrMinus ? 395 : siteKey),
        commitsList: () =>
          siteModules.commitsList(proxyApi, { orgKey: siteOrganizationKey }),
        nil100List: () =>
          siteModules.nil100List(proxyApi, { orgKey: siteOrganizationKey }),
        schedule: () =>
          siteModules.schedule(proxyApi, {
            orgKey: siteOrganizationKey,
            sportKey: 1,
          }),
        trendingList: () => getTrendingLists(),
      };
    case SiteTemplate.HighSchool:
      return {
        featuredArticles: () =>
          siteModules.featuredArticles(proxyApi, siteCategoryKey, {
            timeout: 2000,
          }),
        trendingList: () => getTrendingLists(),
        newsArticles: () =>
          siteModules.siteArticlesByCategory(proxyApi, {
            siteKey,
            type: CategoryTypes.Home,
            limit: 13,
          }),
        siteForums: () => siteModules.siteForums(proxyApi, siteKey),
        topPlayerRankings: () =>
          siteModules.topPlayerRankings(proxyApi, {
            sportKey: params?.sportKey || 1,
            year: params?.year || new Date().getFullYear(),
            stateAbbr: params?.stateAbbr || undefined,
            limit: 5,
          }),
        topTeamRankings: () =>
          siteModules.topTeamRankings(proxyApi, {
            orgType: OrganizationType.HighSchool,
            sportKey: params?.sportKey || 1,
            year: params?.year || new Date().getFullYear(),
            stateKey: params?.stateKey || undefined,
            items: 10,
          }),
      };
    default:
      return {};
  }
};

const getSiteState = (siteKey: number): StateResponse | undefined => {
  const stateData = [
    {
      key: 1,
      name: 'Alabama',
      abbreviation: 'AL',
      sites: [459],
    },
    {
      key: 2,
      name: 'Alaska',
      abbreviation: 'AK',
      sites: [],
    },
    {
      key: 3,
      name: 'Arizona',
      abbreviation: 'AZ',
      sites: [460],
    },
    {
      key: 4,
      name: 'Arkansas',
      abbreviation: 'AR',
      sites: [],
    },
    {
      key: 5,
      name: 'California',
      abbreviation: 'CA',
      sites: [472],
    },
    {
      key: 6,
      name: 'Colorado',
      abbreviation: 'CO',
      sites: [461],
    },
    {
      key: 7,
      name: 'Connecticut',
      abbreviation: 'CT',
      sites: [],
    },
    {
      key: 8,
      name: 'Delaware',
      abbreviation: 'DE',
      sites: [],
    },
    {
      key: 9,
      name: 'District Of Columbia',
      abbreviation: 'DC',
      sites: [],
    },
    {
      key: 10,
      name: 'Florida',
      abbreviation: 'FL',
      sites: [462],
    },
    {
      key: 11,
      name: 'Georgia',
      abbreviation: 'GA',
      sites: [463],
    },
    {
      key: 12,
      name: 'Hawaii',
      abbreviation: 'HI',
      sites: [],
    },
    {
      key: 13,
      name: 'Idaho',
      abbreviation: 'ID',
      sites: [],
    },
    {
      key: 14,
      name: 'Illinois',
      abbreviation: 'IL',
      sites: [464],
    },
    {
      key: 15,
      name: 'Indiana',
      abbreviation: 'IN',
      sites: [465],
    },
    {
      key: 16,
      name: 'Iowa',
      abbreviation: 'IA',
      sites: [466],
    },
    {
      key: 17,
      name: 'Kansas',
      abbreviation: 'KS',
      sites: [],
    },
    {
      key: 18,
      name: 'Kentucky',
      abbreviation: 'KY',
      sites: [],
    },
    {
      key: 19,
      name: 'Louisiana',
      abbreviation: 'LA',
      sites: [],
    },
    {
      key: 20,
      name: 'Maine',
      abbreviation: 'ME',
      sites: [],
    },
    {
      key: 21,
      name: 'Maryland',
      abbreviation: 'MD',
      sites: [467],
    },
    {
      key: 22,
      name: 'Massachusetts',
      abbreviation: 'MA',
      sites: [],
    },
    {
      key: 23,
      name: 'Michigan',
      abbreviation: 'MI',
      sites: [],
    },
    {
      key: 24,
      name: 'Minnesota',
      abbreviation: 'MN',
      sites: [],
    },
    {
      key: 25,
      name: 'Mississippi',
      abbreviation: 'MS',
      sites: [],
    },
    {
      key: 26,
      name: 'Missouri',
      abbreviation: 'MO',
      sites: [],
    },
    {
      key: 27,
      name: 'Montana',
      abbreviation: 'MT',
      sites: [],
    },
    {
      key: 28,
      name: 'Nebraska',
      abbreviation: 'NE',
      sites: [468],
    },
    {
      key: 29,
      name: 'Nevada',
      abbreviation: 'NV',
      sites: [],
    },
    {
      key: 30,
      name: 'New Hampshire',
      abbreviation: 'NH',
      sites: [],
    },
    {
      key: 31,
      name: 'New Jersey',
      abbreviation: 'NJ',
      sites: [469],
    },
    {
      key: 32,
      name: 'New Mexico',
      abbreviation: 'NM',
      sites: [470],
    },
    {
      key: 33,
      name: 'New York',
      abbreviation: 'NY',
      sites: [471],
    },
    {
      key: 34,
      name: 'North Carolina',
      abbreviation: 'NC',
      sites: [473],
    },
    {
      key: 35,
      name: 'North Dakota',
      abbreviation: 'ND',
      sites: [],
    },
    {
      key: 36,
      name: 'Ohio',
      abbreviation: 'OH',
      sites: [474],
    },
    {
      key: 37,
      name: 'Oklahoma',
      abbreviation: 'OK',
      sites: [],
    },
    {
      key: 38,
      name: 'Oregon',
      abbreviation: 'OR',
      sites: [],
    },
    {
      key: 39,
      name: 'Pennsylvania',
      abbreviation: 'PA',
      sites: [475],
    },
    {
      key: 40,
      name: 'Rhode Island',
      abbreviation: 'RI',
      sites: [],
    },
    {
      key: 41,
      name: 'South Carolina',
      abbreviation: 'SC',
      sites: [],
    },
    {
      key: 42,
      name: 'South Dakota',
      abbreviation: 'SD',
      sites: [],
    },
    {
      key: 43,
      name: 'Tennessee',
      abbreviation: 'TN',
      sites: [476],
    },
    {
      key: 44,
      name: 'Texas',
      abbreviation: 'TX',
      sites: [365, 477],
    },
    {
      key: 45,
      name: 'Utah',
      abbreviation: 'UT',
      sites: [],
    },
    {
      key: 46,
      name: 'Vermont',
      abbreviation: 'VT',
      sites: [],
    },
    {
      key: 47,
      name: 'Virginia',
      abbreviation: 'VA',
      sites: [478],
    },
    {
      key: 48,
      name: 'Washington',
      abbreviation: 'WA',
      sites: [479],
    },
    {
      key: 49,
      name: 'West Virginia',
      abbreviation: 'WV',
      sites: [],
    },
    {
      key: 50,
      name: 'Wisconsin',
      abbreviation: 'WI',
      sites: [],
    },
    {
      key: 51,
      name: 'Wyoming',
      abbreviation: 'WY',
      sites: [],
    },
  ] as (StateResponse & { sites: number[] })[];

  const state = stateData.find((s) => s.sites.includes(siteKey));

  return state;
};

const hasSiteSportCategory = (
  categories: SiteCategoryResponse[],
  categoryType: CategoryTypes,
  sportName: string,
) => {
  return categories.some(
    (sc) => sc.categoryType === categoryType && sc.sportName === sportName,
  );
};

export {
  getSiteDataByTemplateType,
  getSiteState,
  hasSiteSportCategory,
  siteModules,
};
